import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ActivePlayType, PlayOption } from '@shared/flow/play-catalog';
import type { GongFlowSummary } from '@shared/flow/gong-flow-catalog';
import { Button } from '@clay-run/glaze/button';
import { DropdownLabel, DropdownSeparator, dropdownItemStyles } from '@clay-run/glaze/dropdown/index';
import { InputField } from '@clay-run/glaze/input-field';
import { Text } from '@clay-run/glaze/text';
import { useApi } from '../../../lib/api';
import { FLOW_ICONS } from '../icons';
import { GongFlowBrowse } from './GongFlowBrowse';
import { useGongFlowBrowseController, fetchGongFlowSteps } from './gong-flow-queries';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * Inline searchable play picker (U4).
 *
 * Matches the locked prototype's `playGroupsHtml` (play-type heading, then
 * its plays) except there is only one live play type today
 * (`event_invite`), so a single heading renders. Checkpoint decision
 * (2026-07-21): NO suggested-first — `plays` renders in the catalog order
 * the caller passed in (`deriveLivePlays` order), never re-sorted here.
 *
 * Click a play row = assign (single-play v0 scope). Multi-select
 * (checkbox -> "Assign to N plays", duplicating the contact across N
 * enrollment_items) is deferred — see the U4 implementation notes.
 *
 * `onAddUnassigned` (flow-contacts-bulk-send U7, decision 14), when passed,
 * renders an always-available "Add to Queue (unassigned)" row below the
 * catalog — independent of `showGongGroup`/catalog state, unlike the
 * Gong-specific "Browse all Gong flows →" row.
 */
/** `PlayOption.play_type_slug` value for the singleton Warm Intro play —
 * mirrors `StatePills.tsx`'s `WarmIntroAddButton` (`plays.find(p =>
 * p.play_type_slug === 'warm_intro')`), the existing precedent for matching
 * this play from the frontend catalog without a shared constant (the
 * backend's `WARM_INTRO_SLUG`, `src/services/flow/warm-intro-definition.ts`,
 * isn't importable from this bundle). */
const WARM_INTRO_PLAY_TYPE_SLUG = 'warm_intro';

/** Stable empty default — see `contactHasEmail`'s "always state what it knows"
 * rationale below for why this ISN'T given the same treatment: unlike
 * `contactHasEmail`/`warmIntroAvailable` (per-contact eligibility gates a
 * missing value could silently mis-enforce), `gongFlowsSuggested` is purely
 * additive display data — an omitted prop just means "no suggested-3 loaded
 * yet", identical to the intended cold-start empty render. Defaulting here
 * keeps the 9 pre-existing PlayPicker tests (and any other caller written
 * before U12) passing unmodified. */
const NO_GONG_FLOWS_SUGGESTED: GongFlowSummary[] = [];

/** Stable empty default for `activePlayTypes` — see its own prop doc comment. */
const NO_ACTIVE_PLAY_TYPES: ActivePlayType[] = [];

/**
 * Shared row style for every clickable row in this picker (play rows, Gong
 * rows, and the two footer rows) — U5 (R13), restyling onto Glaze's
 * EXPORTED menu-item style rather than hand-rolled classNames. Deliberately
 * NOT `DropdownItem` (Key Technical Decision 9): that component throws
 * outside a `<Dropdown>` context and closes itself on click, which would
 * break the search box, the keep-open-while-committing contract, and the
 * body-portaled `AutoEmailConfirmModal`'s host-veto contract.
 * `dropdownItemStyles` is tv() (tailwind-merge), so the overrides here win
 * over the base's `px-3 py-2 text-sm` — but only when the override lands in
 * the SAME variant slot as Glaze's class (see `MENU_ROW`'s own
 * `supports-…` / `grid-cols-…` override below for the case where it
 * doesn't). `focus-visible:bg-bg-raised-hover` is required because
 * `menuItem`'s base sets `focus:outline-hidden`, assuming a `<Dropdown>`'s
 * roving `data-highlighted` focus model that this hand-rolled list doesn't
 * have.
 */
const MENU_ROW = dropdownItemStyles({
  className: [
    'w-full px-2 py-1.5 text-xs font-normal focus-visible:bg-bg-raised-hover',
    // Glaze's `menuItem` opts into `grid-cols-subgrid` under `@supports`,
    // assuming a `menuPopup`/section parent that is itself a grid. Nothing in
    // this hand-rolled list is a grid parent, so in Chromium/Firefox/Safari
    // `subgrid` on a non-grid-item computes as `none`: `DropdownLabel`
    // (`col-start-2`) then gets an EMPTY implicit column 1 that `stretch`
    // sizes equal to the label's — half the row as a blank gutter, the label
    // truncating at half width (Codex round-2 P2; Spencer's screenshot).
    // Re-declare the explicit three-column template in the SAME variant slot
    // so tailwind-merge drops Glaze's `supports-…:grid-cols-subgrid` (a bare
    // `grid-cols-*` here would only replace the bare base and lose in CSS —
    // verified against tailwind-variants 3.3.1 / tailwind-merge 3.5.0).
    // Column 1 (`auto`, no icon) collapses to 0 once `1fr` has taken the free
    // space, so labels sit flush at `px-2`, aligned with the group headings.
    'supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_auto]',
  ].join(' '),
});

/**
 * The ONE width decision for the V2 contact band's two menus — the "Enroll ▾"
 * play picker (`shell-next/tables/ContactEnrollTrigger.tsx`; also
 * `contact/BulkEnrollPopover.tsx`) and the "Actions ▾" instant-actions menu
 * (`shell-next/tables/ContactActionsDropdown.tsx`). 680px (Spencer,
 * 2026-10-06): Gong flow names like "[AQ] Customer — Renewal Check-in Q3" were
 * truncating at `w-72`; the instant-actions menu matches so the band's two
 * dropdowns read as one system (Spencer, 2026-10-07). `!w-*` because BOTH
 * Glaze hosts compose classes with plain `clsx(className, …)` and append
 * their own `w-max` AFTER the caller's class — `PopoverPanel`
 * (`node_modules/@clay-run/glaze/src/popover.tsx`) and `DropdownMenu`
 * (`src/dropdown/dropdown.tsx`, via `menuPopup` in
 * `src/internal/shared-menu/menu-styles.ts`) — so a non-important width loses
 * on stylesheet order (Codex round 2 measured 226px for `w-72`; the
 * dropdown's `w-64` lost the same way and went content-wide). Same reason
 * `chat/ChatHeaderV2.tsx` uses `!overflow-visible` on its panels. A standalone
 * literal so Tailwind's scanner sees the candidate as written.
 */
export const CONTACT_MENU_WIDTH_CLASS = '!w-[680px]';

/**
 * Panel class for the two Glaze `PopoverPanel` hosts of this picker
 * (`shell-next/tables/ContactEnrollTrigger.tsx`, `contact/BulkEnrollPopover.tsx`).
 * Width is `CONTACT_MENU_WIDTH_CLASS` above. Never wider than the viewport:
 * Base UI's Positioner caps the panel at `max-w-(--available-width)` (viewport
 * minus 2×12px `collisionPadding`), and `max-width` beats `width` even when the
 * width is `!important`. `[--popover-padding:0px]` hands the padding to the
 * picker's own `frameless` `p-1.5` (Popover-only — `menuPopup` reads
 * `--dropdown-padding`, which is why the dropdown takes the width constant
 * alone). Both halves are whole literal strings; the template only
 * concatenates, so there is nothing Tailwind has to evaluate.
 */
export const PLAY_PICKER_POPOVER_PANEL_CLASS = `${CONTACT_MENU_WIDTH_CLASS} [--popover-padding:0px]`;

export function PlayPicker({
  plays,
  activePlayTypes = NO_ACTIVE_PLAY_TYPES,
  gongFlowsSuggested = NO_GONG_FLOWS_SUGGESTED,
  onAssign,
  disabled,
  disabledReason,
  ariaLabelPrefix,
  contactHasEmail,
  warmIntroAvailable,
  contactLabel,
  backLabel,
  onBrowseAll,
  initialBrowseOpen,
  onAddUnassigned,
  playRowLabel,
  playRowDisabled,
  addUnassignedLabel,
  onBrowseOpenChange,
  onAutoEmailConfirmOpenChange,
  frameless = false,
}: {
  plays: PlayOption[];
  /**
   * R5e (PR #1312 review §7-3 / plan `2026-08-22-001` U8) — one entry per
   * active+enabled play TYPE, independent of `plays`' per-instance rows.
   * `gongCatalogEntry` below reads THIS list (not `plays`) so the Gong
   * section still renders when gong_flow is live but its mirror is empty
   * (the bootstrap trap `plays`-scanning had). Defaults to `[]` — same
   * cold-start-safe treatment as `gongFlowsSuggested` below — so the ~9
   * pre-existing non-Gong PlayPicker tests (and any caller written before
   * this bead) keep passing unmodified; both real mounts
   * (`ContactActionMenus.tsx`, `QueueBody.tsx`) always pass the real list
   * from `useEnrollQueue()`.
   */
  activePlayTypes?: ActivePlayType[];
  /**
   * The rep's own Tier-1 suggested-3 Gong flows (U12; `EnrollQueueContext`'s
   * `gongFlowsSuggested`, GET /api/flow/gong/flows/suggested) — NOT sourced
   * from `plays` itself. `plays` DOES include one entry per live gong_flow
   * instance (the exposure deriver enumerates the whole mirror, ~660 rows),
   * but those are used here ONLY to detect that the gong_flow play type is
   * live and to read its real play_type_id/name/has_editable_copy/
   * requires_email (see `gongCatalogEntry` below) — never rendered as
   * individual rows the way event_invite/warm_intro plays are (Do NOT
   * create: a full-catalog list of ~660 gong_flow rows in THIS picker's own
   * search/group list). Distinct from the browse OVERLAY's own client-side
   * full-catalog search (`gong-flow-queries.ts`'s `useGongFlowBrowseController`)
   * — that one is a deliberate round-5 fix (QA doc §2, plan `2026-08-20-001`
   * Decisions #1/#7/#8) for the browse panel specifically, not a reopening of
   * this rule.
   */
  gongFlowsSuggested?: GongFlowSummary[];
  /**
   * Auto-open-editor-after-Browse-pick follow-up (2026-08-22) — `opts` is
   * passed ONLY when the play was selected through the browse overlay
   * (`opts.viaBrowse: true`); every other assign path (the generic per-play
   * rows, the "Gong Flows — suggested" quick-pick rows) calls this with a
   * single argument, byte-identical to before this bead, so existing
   * callers/tests asserting a one-arg call are unaffected. Callers that
   * don't care about the distinction (e.g. `ContactActionMenus.tsx`) can
   * keep their existing one-param signature — JS callbacks may always
   * ignore trailing arguments.
   */
  onAssign: (play: PlayOption, opts?: { viaBrowse?: boolean }) => void;
  disabled?: boolean;
  disabledReason?: string;
  /** Prefix for each row's accessible name, e.g. "Assign to". */
  ariaLabelPrefix: string;
  /**
   * Does the contact this picker is assigning FOR have an email on file?
   * Required, not defaulted (same reasoning as `toWireItem`'s required
   * `contactLinkedin` param) — every mount must state what it knows rather
   * than silently assuming "email available". When `false`, plays whose
   * `requires_email` is `true` are filtered out of the list (advisory only
   * — the server gates below are the real enforcement; see this file's
   * header for why a malformed/undefined `requires_email` degrades to
   * "shown").
   */
  contactHasEmail: boolean;
  /**
   * Does this contact currently have an AVAILABLE warm intro (a connector
   * with a non-blank name, not already queued/sent within the last 30 days
   * — the same suppression `warmIntroSource.resolve()` applies to the
   * "Terra Actions" pill, `src/services/flow/contact-state-sources.ts`)?
   * Same shape as `contactHasEmail` — a per-contact advisory computed by the
   * caller from data it already has (the contact's `useContactStateDetail`
   * facts), NOT a property of the play catalog itself (the catalog is
   * global/contact-agnostic — see `play-catalog.ts`'s module doc). Bug fix,
   * Spencer report 2026-08-13: the Enroll dropdown listed "Warm Intro" for
   * every contact whenever the play type was active, with no per-contact
   * eligibility check at all. Defaults `true` while the caller's own state
   * read is still loading — same "degrade to shown" rule as
   * `contactHasEmail`'s malformed-response case, so a slow/failed fetch
   * never hides a play that might actually be available.
   */
  warmIntroAvailable: boolean;
  /**
   * Display name/identity of the contact this picker is assigning FOR
   * (round-5 plan `2026-08-20-001` U3, QA doc §1) — threaded straight into
   * the browse panel's "Assigning {name}" header subtitle
   * (`GongFlowBrowse`'s `contactLabel`). `QueueBody.tsx` passes
   * `contactDisplayIdentity(item)`; `ContactActionMenus.tsx`'s Enroll
   * dropdown passes its own `contactName`. Optional — omitted renders no
   * subtitle (both real mounts pass one; this only degrades for a caller
   * that hasn't been updated yet).
   */
  contactLabel?: string;
  /**
   * Threaded straight into the browse panel's back-control label
   * (`GongFlowBrowse`'s `backLabel` — see that prop's doc comment). Only
   * `QueueBody.tsx` (the dock-mounted picker) passes a value (`"← Queue"`);
   * the contact-card mount leaves this unset and gets `GongFlowBrowse`'s
   * own default ("Close").
   */
  backLabel?: string;
  /**
   * Caller override for the "Browse all Gong flows →" click. When passed,
   * the click is delegated ENTIRELY to the caller and this picker's own
   * full-panel browse overlay (`ConnectedGongFlowBrowse`, portaled to
   * document.body at the sidebar dock's width) is never opened from this
   * mount — `browseOpen` simply never becomes true. Omitted (the
   * `QueueBody.tsx` dock mounts), the button opens the overlay in place,
   * exactly as before. Added for `ContactActionMenus.tsx`'s Enroll
   * dropdown (a popover panel; see `PLAY_PICKER_POPOVER_PANEL_CLASS`):
   * spawning the half-screen overlay from
   * there was jarring, so that caller enqueues the contact unassigned and
   * routes the rep to the sidebar's own picker instead.
   */
  onBrowseAll?: () => void;
  /**
   * "Browse all Gong flows" enqueue-then-drill-in follow-up (2026-08-21) —
   * seeds `browseOpen` so this picker's browse panel starts already open,
   * for the queue row `UnassignedRow` auto-reveals right after an enqueue
   * (see `QueueFlashRequest.autoOpenPicker`). Only takes effect at the
   * moment of a FRESH `PlayPicker` mount (`useState`'s initializer runs
   * once) — exactly the timing `UnassignedRow` needs: it passes this true
   * only while first revealing the picker for the flashed contact.
   * Omitted/`false` preserves the existing closed-by-default behavior.
   */
  initialBrowseOpen?: boolean;
  /**
   * "Add to Queue (unassigned)" (flow-contacts-bulk-send U7, decision 14) —
   * an always-available, explicit escape hatch that enqueues the contact
   * this picker is FOR with no play chosen yet (the existing no-playType
   * `addContact(contactId, { sourceContext })` call `WorkflowsDropdown` in
   * `ContactActionMenus.tsx` already makes — no new API route, no new server
   * logic). Rendered as its own row whenever this is passed, REGARDLESS of
   * `showGongGroup`/catalog state (unlike "Browse all Gong flows →", which
   * only exists inside the Gong section) — generalizing the narrow path that
   * used to be reachable ONLY via that Gong-specific button. Omitted (the
   * `QueueBody.tsx` dock mount, which is always assigning FOR an
   * already-unassigned item) renders nothing extra — there is no "unassign"
   * concept to offer there.
   */
  onAddUnassigned?: () => void;
  /** Bulk-enroll's per-play custom row label (flow-contacts-bulk-send D1) —
   *  e.g. "Add 5 to <play>" / the over-cap "Add first 100 of N…" variant /
   *  the in-flight "Adding N…" variant. Omitted (every existing caller)
   *  renders `play.name` as before — this must be a strictly additive,
   *  zero-behavior-change-by-default prop.
   *
   *  flow-contacts-bulk-send Gong parity (U1) — also honored by the
   *  "Gong Flows — suggested" rows below (previously ONLY `renderGroup`'s
   *  generic rows read this), replacing the row's single name line (the rows
   *  used to carry a send-count/step-count subtitle; removed 2026-10-06 —
   *  reps do not need inline 30-day stats). Same additive contract: omitted,
   *  suggested-row rendering stays byte-identical to before this bead. */
  playRowLabel?: (play: PlayOption) => string;
  /** Bulk-enroll's per-play disable predicate (D1) — e.g. `eligible.length
   *  === 0 || readOnly || committingKey !== null`. ORed with the existing
   *  top-level `disabled` prop (a row is disabled if EITHER is true); the
   *  row's `title` still follows only the top-level `disabled`/`disabledReason`
   *  pair, unchanged. Omitted defaults to `false` for every row (no
   *  additional gating beyond the existing top-level `disabled`).
   *
   *  flow-contacts-bulk-send Gong parity (U1) — also honored by the
   *  "Gong Flows — suggested" rows below, ORed with `disabled` the same way
   *  `renderGroup` already does it. Does NOT gate the "Browse all Gong
   *  flows →" button itself — that row isn't a play row, so it keeps
   *  following only the top-level `disabled` prop. */
  playRowDisabled?: (play: PlayOption) => boolean;
  /** Overrides the hardcoded "Add to Queue (unassigned)" row text (D1) — bulk
   *  passes "Add N as unassigned". Omitted keeps today's literal string. Has
   *  no effect unless `onAddUnassigned` is also passed (that row doesn't
   *  render at all otherwise — unchanged). */
  addUnassignedLabel?: string;
  /**
   * flow-contacts-bulk-send Gong parity (U1) — fired whenever this picker's
   * OWN `browseOpen` state changes (the "Browse all Gong flows →" click that
   * opens the default overlay, and the overlay's own `onClose`). Never fired
   * when `onBrowseAll` is passed (that path delegates the click entirely to
   * the caller and `browseOpen` never becomes true here — see `onBrowseAll`'s
   * own doc comment). Added so a caller whose own dismissal machinery can't
   * see the browse overlay (it portals to `document.body`, outside any
   * `menuRef` a caller might track) can suppress an outside-click/Escape
   * close while the panel is open. Default no-op — strictly additive, same
   * zero-behavior-change-by-default contract as `playRowLabel`/
   * `playRowDisabled` above.
   */
  onBrowseOpenChange?: (open: boolean) => void;
  /**
   * Fired whenever this picker's auto-email confirmation (`AutoEmailConfirmModal`)
   * opens (`true`) or is answered/dismissed (`false`). The modal portals to
   * `document.body` — it has to, because a Glaze `PopoverPanel` host applies
   * `scale`/`backdrop-blur` and so is a containing block for `position: fixed`;
   * rendered inline, the modal's `inset-0` backdrop covered only the popup and
   * left the rest of the page clickable (Codex round-2 P0). Once outside the
   * host's DOM subtree, every press/focus inside the modal reads as an "outside
   * press" to Base UI's `useDismiss` / `FloatingFocusManager` and to
   * `usePortalDropdown`'s outside-click layer, which would close the host, unmount
   * this picker, and unmount the modal under the rep's pointer — so a host with
   * any outside-press dismissal MUST veto its close requests while this is `true`
   * (see `BulkEnrollPopover`'s `canClose`, `ContactEnrollTrigger`'s `onOpenChange`,
   * `ContactActionMenus`' `usePortalDropdown({ canClose })`). Hosts with no
   * outside-press dismissal (`QueueBody`, `AssignedRow`, `CustomInvitePanel`,
   * `rail/*`) ignore it. Same additive, default-no-op contract as
   * `onBrowseOpenChange` above (which exists for the identical reason — the
   * browse panel is also body-portaled).
   */
  onAutoEmailConfirmOpenChange?: (open: boolean) => void;
  /**
   * U5 (R13) — drop this picker's own grey-box container (`rounded-lg
   * bg-bg-secondary p-3`) for mounts hosted inside a surface that already
   * supplies the raised chrome, e.g. a Glaze `PopoverPanel`
   * (`ContactEnrollTrigger.tsx`, `BulkEnrollPopover.tsx` — U5b). The ~7 bare
   * inline mounts (`ContactActionMenus`, `AssignedRow`, `CustomInvitePanel`,
   * `QueueBody`, `rail/InvitePane`, `rail/LeadRow`, `rail/UnassignedRow`)
   * omit this and keep the grey box — they have no other surface to sit on.
   * Default `false` — strictly additive, same zero-behavior-change-by-
   * default contract as `playRowLabel`/`playRowDisabled` above.
   */
  frameless?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [browseOpen, setBrowseOpen] = useState(initialBrowseOpen ?? false);
  const { apiFetch } = useApi();

  /**
   * Auto-email pre-assign gate: a Gong flow whose first step is Auto Email
   * (`AUTO_SEND_EMAIL`) sends that email itself, unattended, the moment the
   * flow is assigned — unlike every other step type, which just queues the
   * step for the rep or a later Gong-side trigger. `GongFlowSummary` (the
   * suggested-3 / browse row shape) carries no step-level detail, only
   * `step_count`, so the ONLY way to know is to fetch the flow's own steps
   * (`fetchGongFlowSteps`, a PLAIN fetch that deliberately bypasses
   * `useGongFlowSteps`'s react-query cache — see its doc comment in
   * `gong-flow-queries.ts` for the no-QueryClientProvider constraint).
   *
   * This wraps ALL assign clicks (suggested rows, browse-overlay `onSelect`,
   * and the generic per-play rows) rather than adding a second assign path
   * — plays without step overrides (`ui.has_step_overrides`, U5B — was a
   * `play_type_slug` comparison) short-circuit on the capability check
   * below with no fetch.
   * A steps-fetch failure fails OPEN (assigns anyway): this is an advisory
   * warning, not an enforcement gate, matching this file's existing
   * degrade-to-shown pattern for `requires_email`/`warmIntroAvailable`.
   */
  const [pendingAutoEmailConfirm, setPendingAutoEmailConfirm] = useState<{ play: PlayOption; viaBrowse: boolean } | null>(null);
  /**
   * Race guard for the auto-email steps-check above: bumped on every
   * gong-flow assign click AND on every modal decision (confirm/cancel).
   * Any in-flight `fetchGongFlowSteps` whose captured nonce no longer
   * matches this ref's current value is superseded and must drop its
   * outcome silently — otherwise a stale resolution (e.g. from a flow the
   * rep clicked before double-clicking, or clicked and then re-clicked)
   * can reopen the modal, or double-assign, after a newer click (or an
   * explicit Cancel) already resolved the picker's state. Mirrors the
   * house pattern for this exact problem: `EnrollQueueContext.tsx`'s
   * `jumpNonceRef` (capture-then-compare across an async gap).
   *
   * Mutated only inside event handlers (click handlers below, and the
   * modal's onConfirm/onCancel), never inside an effect — the
   * StrictMode-double-invoke reset-at-mount rule (CLAUDE.md
   * "StrictMode-Resilient Effects") is about refs written by effects and
   * doesn't apply here.
   */
  const assignNonceRef = useRef(0);
  /**
   * `viaBrowse` (default `false`) — set only by the browse overlay's own
   * selection (`ConnectedGongFlowBrowse`'s `onSelect` below), never by the
   * generic per-play rows or the "Gong Flows — suggested" quick-pick rows
   * (`renderGroup`/the suggested-flow button both call this with ONE
   * argument, so `viaBrowse` defaults `false` for them). Threaded through to
   * `onAssign` as a SECOND argument ONLY when true — see `onAssign`'s own
   * prop doc comment for why the false/omitted case must stay a one-arg
   * call (existing callers/tests assert exactly that).
   */
  const handleAssign = useCallback((play: PlayOption, viaBrowse = false) => {
    const finalize = (p: PlayOption) => {
      if (viaBrowse) onAssign(p, { viaBrowse: true });
      else onAssign(p);
    };
    if (!play.ui?.has_step_overrides) {
      // Deliberately NOT bumping assignNonceRef here — this branch stays
      // synchronous and untouched by the race guard. Residual gap (out of
      // scope for this fix): if a gong-flow steps-check is in-flight and
      // the rep then clicks a NON-gong play, the gong resolution can still
      // land afterward and assign/open the modal. Closing that would just
      // be `assignNonceRef.current += 1;` before `onAssign(play)` below —
      // left out because it's outside the reviewed bug's scope.
      finalize(play);
      return;
    }
    const nonce = ++assignNonceRef.current;
    void fetchGongFlowSteps(apiFetch, play.play_instance_ref)
      .then(({ steps }) => {
        if (nonce !== assignNonceRef.current) return;
        const isAutoEmailFirst = steps.find(s => s.step_order === 1)?.step_type === 'AUTO_SEND_EMAIL';
        if (isAutoEmailFirst) {
          setPendingAutoEmailConfirm({ play, viaBrowse });
          onAutoEmailConfirmOpenChange?.(true);
        } else {
          finalize(play);
        }
      })
      .catch(() => {
        if (nonce !== assignNonceRef.current) return;
        finalize(play);
      });
  }, [onAssign, apiFetch, onAutoEmailConfirmOpenChange]);
  // Extracted (rather than inline in the modal's JSX) so both handlers can
  // bump assignNonceRef. Strictly, under today's code the bump here is
  // unreachable hardening — the guard above already means the modal can
  // only be showing the CURRENT-nonce resolution, and that fetch is by
  // definition already complete once the modal is on screen, so no live
  // fetch can survive into this state. The bump makes the invariant local
  // rather than emergent: "once the rep has answered (or dismissed) the
  // confirm, every outstanding steps-check outcome is void" holds even if
  // a future code path opens the modal while another fetch is in flight.
  const handleAutoEmailCancel = useCallback(() => {
    assignNonceRef.current += 1;
    setPendingAutoEmailConfirm(null);
    onAutoEmailConfirmOpenChange?.(false);
  }, [onAutoEmailConfirmOpenChange]);
  const handleAutoEmailConfirm = useCallback(() => {
    assignNonceRef.current += 1;
    if (!pendingAutoEmailConfirm) return;
    const { play, viaBrowse } = pendingAutoEmailConfirm;
    if (viaBrowse) onAssign(play, { viaBrowse: true });
    else onAssign(play);
    setPendingAutoEmailConfirm(null);
    onAutoEmailConfirmOpenChange?.(false);
  }, [onAssign, pendingAutoEmailConfirm, onAutoEmailConfirmOpenChange]);

  // gong_flow is rendered as its OWN hand-built section below, fed by
  // `gongFlowsSuggested` + the browse overlay — never through the generic
  // per-play grouping/search pipeline (Do NOT create: rendering the exposure
  // deriver's ~660 individual `plays` entries for gong_flow as rows in THIS
  // picker's own list — the browse overlay's OWN client-side full-catalog
  // search over its own fetched `flows` array, added round-5, is a separate
  // deliberate mechanism; see `gong-flow-queries.ts`). One representative
  // entry (`gongCatalogEntry`) is kept so the section can read the type's
  // REAL play_type_id/name/has_editable_copy/requires_email off it (never
  // guessed locally) — see `buildGongFlowPlayOption` below.
  //
  // U5B — identification is now driven by the `ui.own_picker_section`
  // capability (was a `play_type_slug === 'gong_flow'` comparison), so a
  // future second play type marked as owning its own picker section (this
  // file's hand-built rendering below is still Gong-specific copy/wiring —
  // see the "Gong Flows — suggested" JSX; a real second own-section play
  // type would need its own dedicated section, not a relabeling of this
  // one) is at least correctly EXCLUDED from the generic list here rather
  // than silently falling through as a normal row.
  const nonGongPlays = useMemo(() => plays.filter(p => !p.ui?.own_picker_section), [plays]);
  // R5e — read off `activePlayTypes` (one entry per active+enabled TYPE,
  // regardless of derived-instance count), NOT `plays` (which is empty for
  // gong_flow on a fresh install with zero mirrored flows — the bootstrap
  // trap this bead fixes; see the `activePlayTypes` prop doc comment above).
  const gongCatalogEntry = useMemo(
    () => activePlayTypes.find(t => t.ui?.own_picker_section),
    [activePlayTypes],
  );

  // Advisory filter for a no-email contact: hide any play whose connector
  // REQUIRES an email (play-capabilities.ts's derivation, riding along on
  // PlayOption.requires_email). Computed off the FULL (non-Gong) list,
  // before the search filter, so `hiddenForNoEmail`'s count doesn't flicker
  // while typing. Only `requires_email === true` hides a play — `undefined`
  // (a malformed catalog response; `validateResponse` is observational) or
  // `false` both show it, so a bad server response degrades to "shown, then
  // the server's own 400 + global toast enforces" rather than hiding
  // everything.
  const emailFiltered = contactHasEmail ? nonGongPlays : nonGongPlays.filter(p => !p.requires_email);
  // Same shape, second advisory: hide the Warm Intro play specifically when
  // THIS contact has no available warm intro. Unlike `requires_email` (a
  // property of the play/connector, true for every contact alike), warm-
  // intro availability is per-CONTACT, so it can't ride on `PlayOption`
  // itself — the caller passes what it already knows.
  const assignable = warmIntroAvailable
    ? emailFiltered
    : emailFiltered.filter(p => p.play_type_slug !== WARM_INTRO_PLAY_TYPE_SLUG);
  const hiddenForNoEmail = nonGongPlays.length - emailFiltered.length;

  // Same no-email advisory, applied once at the GROUP level for Gong (its
  // `requires_email` is type-wide, identical for every flow) rather than
  // per-row — there is currently no gong_flow field sourced from
  // `contact.email` (gong-flow-definition.ts), so this branch is defensive,
  // not yet reachable in practice. Deliberately NOT folded into
  // `hiddenForNoEmail`'s count: that badge counts individual hidden PLAYS,
  // and "Gong Flows" hidden as a whole group isn't the same unit.
  const gongHiddenForNoEmail = !!gongCatalogEntry && !contactHasEmail && gongCatalogEntry.requires_email;
  const showGongGroup = !!gongCatalogEntry && !gongHiddenForNoEmail;

  // Group by play type, preserving catalog order (checkpoint decision: no
  // re-sorting). One heading PER TYPE — with multiple active play types
  // (Gong etc.), each gets its own group, matching the prototype's
  // playGroupsHtml. (Previously rendered a single heading from plays[0],
  // which would have mislabeled every play under the first type.)
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = q ? assignable.filter(p => p.name.toLowerCase().includes(q)) : assignable;
    const order: number[] = [];
    const byType = new Map<number, { name: string; plays: PlayOption[] }>();
    for (const play of matching) {
      let group = byType.get(play.play_type_id);
      if (!group) {
        group = { name: play.play_type_name, plays: [] };
        byType.set(play.play_type_id, group);
        order.push(play.play_type_id);
      }
      group.plays.push(play);
    }
    return order.map(id => byType.get(id)!);
  }, [assignable, query]);

  // Data-driven insertion point for the Gong Flows section: however many
  // OTHER play-type groups precede gong_flow's play_type_id.
  //
  // U4B (F4, catalog payload trim) — re-derived from `activePlayTypes`'
  // order, not `plays`' order: the live catalog no longer carries ANY
  // per-instance rows for a `catalog: true` exposure kind (today:
  // `gong_flow`), so the old `for (const play of plays)` loop could never
  // find `gongCatalogEntry.play_type_id` in `plays` at all — it would run
  // to completion counting every OTHER type, always landing Gong last
  // regardless of where it actually sits in the catalog's real type order.
  // `activePlayTypes` (one entry per active+enabled TYPE, independent of
  // `plays`' per-instance rows — the same list `gongCatalogEntry` above
  // already reads for the identical "empty mirror" bootstrap reason) still
  // carries gong_flow's real position, so counting `groups` whose type
  // precedes it there reproduces the same "N other type-groups come first"
  // insertion point the old loop computed, without depending on `plays`
  // ever containing a Gong row.
  const gongGroupIndex = useMemo(() => {
    if (!gongCatalogEntry) return -1;
    const gongTypeIndex = activePlayTypes.findIndex(t => t.play_type_id === gongCatalogEntry.play_type_id);
    if (gongTypeIndex === -1) return groups.length;
    const precedingTypeIds = new Set(activePlayTypes.slice(0, gongTypeIndex).map(t => t.play_type_id));
    let index = 0;
    for (const group of groups) {
      const groupTypeId = group.plays[0]?.play_type_id;
      if (groupTypeId === undefined || !precedingTypeIds.has(groupTypeId)) break;
      index += 1;
    }
    return index;
  }, [groups, activePlayTypes, gongCatalogEntry]);
  const splitAt = gongGroupIndex === -1 ? groups.length : gongGroupIndex;

  function renderGroup(group: { name: string; plays: PlayOption[] }) {
    return (
      <div key={group.name}>
        <Text size="xs" variant="secondary" render={<div />} className="truncate px-2 pt-1.5 pb-1">
          {group.name}
        </Text>
        {group.plays.map(play => {
          const isDisabled = disabled || (playRowDisabled?.(play) ?? false);
          return (
            <button
              key={`${play.play_type_id}:${play.play_instance_ref}`}
              type="button"
              disabled={isDisabled}
              data-disabled={isDisabled ? '' : undefined}
              title={disabled ? disabledReason : undefined}
              aria-label={`${ariaLabelPrefix} ${play.name}`}
              onClick={() => handleAssign(play)}
              className={MENU_ROW}
            >
              <DropdownLabel className="truncate">{playRowLabel ? playRowLabel(play) : play.name}</DropdownLabel>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      data-testid="play-picker-root"
      className={
        frameless
          ? 'relative flex flex-col gap-1 p-1.5'
          : 'relative flex flex-col gap-2 rounded-lg bg-bg-secondary p-3'
      }
    >
      <InputField
        {...AUTOFILL_IGNORE_PROPS}
        size="sm"
        type="search"
        aria-label="Search plays"
        placeholder="Search plays…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        leadingIcon={FLOW_ICONS.search}
      />
      {hiddenForNoEmail > 0 && (
        <div
          data-testid="play-picker-no-email-note"
          className="rounded-md bg-bg-orange-light px-2 py-1 text-xs text-content-warning"
        >
          No email on file for this contact — {hiddenForNoEmail} email-only{' '}
          {hiddenForNoEmail === 1 ? 'play is' : 'plays are'} hidden.
        </div>
      )}
      <div className="max-h-64 overflow-y-auto">
        {groups.length === 0 && !showGongGroup && (
          <Text size="xs" variant="tertiary" className="px-1 py-1">
            {hiddenForNoEmail > 0 && query.trim() === ''
              ? 'No plays available for a contact with no email.'
              : 'No plays match'}
          </Text>
        )}
        {groups.slice(0, splitAt).map(renderGroup)}
        {showGongGroup && gongCatalogEntry && (
          <div>
            <Text size="xs" variant="secondary" render={<div />} className="truncate px-2 pt-1.5 pb-1">
              Gong Flows — suggested
            </Text>
            {gongFlowsSuggested.length === 0 && (
              <Text size="xs" variant="tertiary" className="px-1 py-1">
                No flows sent in the last 30 days — browse all flows below.
              </Text>
            )}
            {gongFlowsSuggested.map(flow => {
              // flow-contacts-bulk-send Gong parity (U1) — hoisted so
              // `playRowLabel`/`playRowDisabled` (bulk's per-play copy/
              // gating, D1) can be applied to this row the SAME way
              // `renderGroup` already applies them to its generic rows.
              // Previously this `PlayOption` was only ever constructed
              // inline inside `onClick`, so there was nothing for those two
              // props to be called with here.
              const play = buildGongFlowPlayOption(gongCatalogEntry, flow);
              const isDisabled = disabled || (playRowDisabled?.(play) ?? false);
              return (
                <button
                  key={flow.flow_id}
                  type="button"
                  disabled={isDisabled}
                  data-disabled={isDisabled ? '' : undefined}
                  title={disabled ? disabledReason : undefined}
                  aria-label={`${ariaLabelPrefix} ${flow.name}`}
                  onClick={() => handleAssign(play)}
                  className={MENU_ROW}
                >
                  <DropdownLabel className="truncate">{playRowLabel ? playRowLabel(play) : flow.name}</DropdownLabel>
                </button>
              );
            })}
            <button
              type="button"
              className={MENU_ROW}
              disabled={disabled}
              data-disabled={disabled ? '' : undefined}
              title={disabled ? disabledReason : undefined}
              onClick={() => {
                if (onBrowseAll) {
                  onBrowseAll();
                } else {
                  setBrowseOpen(true);
                  onBrowseOpenChange?.(true);
                }
              }}
            >
              Browse all Gong flows →
            </button>
          </div>
        )}
        {groups.slice(splitAt).map(renderGroup)}
      </div>
      {onAddUnassigned && (
        <>
          <DropdownSeparator />
          <button
            type="button"
            className={MENU_ROW}
            disabled={disabled}
            data-disabled={disabled ? '' : undefined}
            title={disabled ? disabledReason : undefined}
            onClick={() => onAddUnassigned()}
            data-testid="play-picker-add-unassigned"
          >
            {addUnassignedLabel ?? 'Add to Queue (unassigned)'}
          </button>
        </>
      )}
      {browseOpen && gongCatalogEntry && (
        <ConnectedGongFlowBrowse
          catalogEntry={gongCatalogEntry}
          onAssign={handleAssign}
          onClose={() => { setBrowseOpen(false); onBrowseOpenChange?.(false); }}
          contactLabel={contactLabel}
          backLabel={backLabel}
        />
      )}
      {pendingAutoEmailConfirm &&
        createPortal(
          <AutoEmailConfirmModal
            play={pendingAutoEmailConfirm.play}
            onConfirm={handleAutoEmailConfirm}
            onCancel={handleAutoEmailCancel}
          />,
          document.body,
        )}
    </div>
  );
}

/** Same focusable-elements selector `ui/Modal.tsx` uses for its own Tab
 * trap (lines 96–108) — kept as a local const rather than importing from
 * `ui/Modal` (this modal deliberately doesn't import that component; see
 * its own doc comment). */
const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Blocking confirmation for the auto-email pre-assign gate (see `handleAssign`'s
 * doc comment above). Portaled to `document.body` by the caller (see the
 * `pendingAutoEmailConfirm` render in `PlayPicker`): a Glaze `PopoverPanel` host
 * is a CSS containing block (`scale` transition + `backdrop-blur-xl`,
 * `node_modules/@clay-run/glaze/src/popover.tsx`), so an inline `fixed inset-0`
 * backdrop covered only the popup and left the page behind it clickable — a
 * mis-click could dismiss this confirm AND hit the control underneath (Codex
 * round-2 P0). Hosts with outside-press dismissal veto their close requests
 * while this is open via `onAutoEmailConfirmOpenChange`. Still hand-rolled rather
 * than `ui/Modal` or Glaze's `AlertDialog`: one two-button confirm, and nesting
 * a Base UI Dialog inside a Base UI Popover's dismissal tree is its own
 * investigation. The Escape-to-cancel, initial-focus, tab trap, and focus-restore
 * below are the hand-rolled minimum of what `ui/Modal.tsx` provides (its own
 * `useEffect`s around lines 77–142).
 */
function AutoEmailConfirmModal({
  play,
  onConfirm,
  onCancel,
}: {
  play: PlayOption;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const prevFocusRef = useRef<HTMLElement | null>(null);
  // Latest-value ref for onCancel so the mount-only keydown effect below
  // never needs onCancel in its dep array (it would otherwise have to
  // re-subscribe the listener on every render if the parent ever passed a
  // non-memoized callback). With this file's `handleAutoEmailCancel`
  // useCallback, onCancel is already stable — this ref just makes the
  // modal self-sufficient regardless of what the caller passes.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const descriptionId = useId();

  // Mount-only: capture the previously focused element, move focus to
  // Cancel (the safe default — Enter on open can't trigger an unattended
  // send), and restore focus on unmount. StrictMode-safe without extra
  // work: the dev double-invoke runs this effect's cleanup (restoring
  // focus to the original element) synchronously before the second setup
  // re-captures, so the second capture still sees the original element,
  // not the Cancel button.
  useLayoutEffect(() => {
    prevFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    cancelRef.current?.focus();
    return () => {
      try {
        prevFocusRef.current?.focus();
      } catch {
        // Element may no longer be attached/focusable — ignore.
      }
      prevFocusRef.current = null;
    };
  }, []);

  // Mount-only: Escape cancels, Tab/Shift+Tab wrap focus within the dialog.
  useLayoutEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancelRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        el => !el.hasAttribute('disabled'),
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const current = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (current === first || !node.contains(current)) {
          last.focus();
          e.preventDefault();
        }
      } else if (current === last || !node.contains(current)) {
        first.focus();
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="First step is Auto Email"
        aria-describedby={descriptionId}
        className="relative w-80 max-w-full rounded-lg border-[0.5px] border-border-primary bg-bg-primary p-4 shadow-xl"
      >
        <Text size="sm" weight="semibold">
          First step is Auto Email
        </Text>
        <Text id={descriptionId} size="xs" variant="secondary" className="mt-1.5">
          {play.name}'s first step is Auto Email — an email will be sent automatically as soon as you press Send.
        </Text>
        <div className="mt-3 flex items-center justify-end gap-1.5">
          <Button ref={cancelRef} variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm}>
            Add to Queue
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * A selected `GongFlowSummary` (suggested-3 row OR browse row) → the SAME
 * `PlayOption` shape `onAssign` already expects — no new assign path, no
 * second context method (Do NOT create: "no second picker or parallel
 * group-rendering path"). Identity fields (`play_instance_ref`, `name`) come
 * from the specific flow; every other field is read off `catalogEntry` (R5e:
 * the live `active_play_types` kind-marker row for this play type) — REAL
 * server-computed values, never guessed locally (CLAUDE.md "No Hardcoded
 * Fallbacks").
 */
function buildGongFlowPlayOption(catalogEntry: ActivePlayType, flow: GongFlowSummary): PlayOption {
  return {
    play_type_id: catalogEntry.play_type_id,
    play_instance_ref: flow.flow_id,
    name: flow.name,
    play_type_name: catalogEntry.play_type_name,
    play_type_slug: catalogEntry.play_type_slug,
    invite_subject: null,
    invite_body: null,
    has_editable_copy: catalogEntry.has_editable_copy,
    requires_email: catalogEntry.requires_email,
    copy_required: catalogEntry.copy_required,
    // U5B — carry the catalog entry's real `ui` capabilities through, same
    // as every other field here (never guessed locally); this is the
    // client-side construction site `PlayOptionSchema.ui`'s doc comment
    // flags as needing to supply it once U5B lands.
    ui: catalogEntry.ui,
  };
}

/**
 * The browse overlay's connected wrapper — only instantiated while
 * `browseOpen` is true, so the pre-existing PlayPicker tests (which never
 * open it) never require a `QueryClientProvider` ancestor. Composes
 * `useGongFlowBrowseController` (the query/mutation plumbing,
 * `gong-flow-queries.ts`) with the presentational `GongFlowBrowse`.
 */
function ConnectedGongFlowBrowse({
  catalogEntry,
  onAssign,
  onClose,
  contactLabel,
  backLabel,
}: {
  catalogEntry: ActivePlayType;
  /**
   * This is `PlayPicker`'s own `handleAssign` — its `viaBrowse` second
   * parameter defaults `false`, so passing `true` explicitly below (the
   * ONLY call site inside this file that does) is what marks a selection
   * as having come through the browse overlay specifically. See
   * `handleAssign`'s doc comment.
   */
  onAssign: (play: PlayOption, viaBrowse?: boolean) => void;
  onClose: () => void;
  contactLabel?: string;
  backLabel?: string;
}) {
  const controller = useGongFlowBrowseController();
  return (
    <GongFlowBrowse
      {...controller}
      onSelect={flow => {
        onAssign(buildGongFlowPlayOption(catalogEntry, flow), true);
        onClose();
      }}
      onClose={onClose}
      contactLabel={contactLabel}
      backLabel={backLabel}
    />
  );
}
